import React, { useMemo, useRef, useState } from "react";

// Komentáře se zmínkami (27.8.2026). Napsáním „@" vyskočí nabídka lidí, kteří
// u daného úkolu zmínku dostat můžou — seznam posílá backend podle úrovně úkolu
// (viz netlify/functions/_lib/zminky.cjs), takže trenér se v Operations vůbec
// nenabídne. Vybrané jméno se vkládá jako „@Jméno Příjmení"; přesně v týhle
// podobě ho backend v textu hledá, ručně komolit se nesmí.

function norm(s) {
  return String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

// Rozsvítí zmínky v už napsaném komentáři.
export function ZminkaText({ text }) {
  const casti = String(text || "").split(/(@\p{Lu}[\p{L}]+(?: \p{Lu}[\p{L}]+)?)/gu);
  return (
    <>
      {casti.map((c, i) =>
        /^@\p{Lu}/u.test(c)
          ? <span key={i} className="font-semibold text-violet-700 bg-violet-50 rounded px-1">{c}</span>
          : <React.Fragment key={i}>{c}</React.Fragment>
      )}
    </>
  );
}

/**
 * Bezobrazová logika nabídky. Vrací props pro input/textarea a hotovou nabídku.
 * onKeyDown vrací true, když klávesu spotřebovala nabídka — volající pak nesmí
 * dělat nic dalšího (třeba odeslat komentář Enterem).
 */
export function useZminky({ value, onChange, lide = [] }) {
  const [hledani, setHledani] = useState(null); // null = nabídka zavřená
  const [vybrano, setVybrano] = useState(0);
  const ref = useRef(null);

  const navrhy = useMemo(() => {
    if (hledani === null) return [];
    const h = norm(hledani);
    return (lide || []).filter((o) => !h || norm(o.jmeno).includes(h)).slice(0, 6);
  }, [hledani, lide]);

  // Rozpozná rozepsanou zmínku těsně před kurzorem.
  const zjistiZminku = (text, caret) => {
    const pred = String(text || "").substring(0, caret);
    const m = pred.match(/@([\p{L}]*(?: [\p{L}]*)?)$/u);
    return m ? m[1] : null;
  };

  const onChangeVstup = (e) => {
    onChange(e.target.value);
    setHledani(zjistiZminku(e.target.value, e.target.selectionStart || 0));
    setVybrano(0);
  };

  const vloz = (osoba) => {
    const el = ref.current;
    const caret = el ? (el.selectionStart || String(value || "").length) : String(value || "").length;
    const txt = String(value || "");
    const zacatek = txt.substring(0, caret).lastIndexOf("@");
    if (zacatek < 0) return;
    onChange(txt.substring(0, zacatek) + "@" + osoba.jmeno + " " + txt.substring(caret));
    setHledani(null);
    setTimeout(() => {
      if (!el) return;
      const pozice = zacatek + osoba.jmeno.length + 2;
      el.focus();
      el.setSelectionRange(pozice, pozice);
    }, 0);
  };

  const onKeyDown = (e) => {
    if (hledani === null || navrhy.length === 0) return false;
    if (e.key === "ArrowDown") { e.preventDefault(); setVybrano((i) => (i + 1) % navrhy.length); return true; }
    if (e.key === "ArrowUp") { e.preventDefault(); setVybrano((i) => (i - 1 + navrhy.length) % navrhy.length); return true; }
    if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); vloz(navrhy[vybrano] || navrhy[0]); return true; }
    if (e.key === "Escape") { e.preventDefault(); setHledani(null); return true; }
    return false;
  };

  const onBlur = () => { setTimeout(() => setHledani(null), 150); };

  const nabidka = navrhy.length > 0 ? (
    <div className="absolute bottom-full left-0 mb-1 z-30 w-64 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
      {navrhy.map((o, i) => (
        <button key={o.jmeno} type="button" onMouseDown={(ev) => { ev.preventDefault(); vloz(o); }}
          className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${i === vybrano ? "bg-violet-50" : "hover:bg-slate-50"}`}>
          <span className="font-medium text-slate-800 truncate">{o.jmeno}</span>
          {o.role && <span className="ml-auto text-[11px] uppercase tracking-wide text-slate-400">{o.role}</span>}
        </button>
      ))}
    </div>
  ) : null;

  return { ref, onChangeVstup, onKeyDown, onBlur, nabidka, oteveno: navrhy.length > 0 };
}

export default function ZminkaInput({ value, onChange, lide = [], className = "", placeholder = "Napsat komentář…", disabled = false }) {
  const z = useZminky({ value, onChange, lide });
  return (
    <div className="relative flex-1 min-w-0">
      <input ref={z.ref} className={className} placeholder={placeholder} value={value} disabled={disabled}
        onChange={z.onChangeVstup} onKeyDown={z.onKeyDown} onBlur={z.onBlur} />
      {z.nabidka}
    </div>
  );
}
